/**
 * Session-scoped dismiss for the Basic Value onboarding dialog, keyed by fleet.
 * Survives SPA navigation and reloads within the tab, but not closing the tab —
 * once the user closes the dialog it stays dismissed for the rest of the session,
 * reappearing only in a new tab/session while the fleet still has pending features.
 */
const DISMISSED_KEY_PREFIX = 'basic-value-onboarding-dismissed';

const dismissedKey = (fleetId: string) => `${DISMISSED_KEY_PREFIX}:${fleetId}`;

export const isBasicValueDismissed = (fleetId: string): boolean => {
  try {
    return sessionStorage.getItem(dismissedKey(fleetId)) === 'true';
  } catch {
    return false;
  }
};

export const persistBasicValueDismissed = (fleetId: string): void => {
  try {
    sessionStorage.setItem(dismissedKey(fleetId), 'true');
  } catch {
    // sessionStorage unavailable
  }
};

/**
 * Longer "snooze" for the "Não quero mais ver isso" action, keyed by fleet, persisted to
 * localStorage with a 4-day TTL. Survives reloads, new sessions and re-login (no backend); the
 * guide reappears only after the window elapses.
 */
const SNOOZE_KEY_PREFIX = 'basic-value-onboarding-snoozed-until';
const SNOOZE_DURATION_MS = 4 * 24 * 60 * 60 * 1000; // 4 days

const snoozeKey = (fleetId: string) => `${SNOOZE_KEY_PREFIX}:${fleetId}`;

export const isBasicValueSnoozed = (fleetId: string): boolean => {
  try {
    const until = Number(localStorage.getItem(snoozeKey(fleetId)));
    return Number.isFinite(until) && until > 0 && until > Date.now();
  } catch {
    return false;
  }
};

export const persistBasicValueSnooze = (fleetId: string): void => {
  try {
    localStorage.setItem(snoozeKey(fleetId), String(Date.now() + SNOOZE_DURATION_MS));
  } catch {
    // localStorage unavailable
  }
};
